import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RotateCcw, FlaskConical, Loader2, Vibrate, Bluetooth, Check, Mic, Upload, Trash2, AlertTriangle, Clock, Globe, Bug, Play, Volume2, Pause, AlertCircle } from 'lucide-react';
import { fetchUserSettings, updateUserSettings, updateUserConsent, deleteAccount, deleteAllInspections } from '../services/inspectionService';
import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import apiClient from '../services/apiClient';
import DataPrivacyCard from './DataPrivacyCard';
import { useSetting, useVoiceTriggerSetting, useStopTriggerSetting, useAudioUploadSetting, useTriggerLanguageSetting, useChunkDurationSetting, useUploadRawChunksSetting, usePlaybackRawSetting, useNoiseLevelSetting, usePauseDetectionSetting, useExtendedErrorReportsSetting, useBeepVolumeSetting } from '../hooks/useSetting';
import { ENVIRONMENT_PRESETS, getNoiseLevelDb, getPauseThresholdDb } from '../services/settingsService';
import { voiceTriggerService } from '../services/voiceTriggerService';
import LanguageSwitcher from './LanguageSwitcher';
import AppBarMenu from './AppBarMenu';
import Logo from './Logo';
import packageJson from '../../package.json';

// Alert component
const Alert = ({ severity, children }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`px-4 py-3 border rounded-lg ${styles[severity]}`}>
      {children}
    </div>
  );
};

// Format ISO timestamp to locale string
const formatTimestamp = (isoString) => {
  if (!isoString) return '-';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    const dateStr = date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return `${dateStr} ${timeStr}`;
  } catch {
    return isoString;
  }
};

// Default enrichment prompt
const DEFAULT_ENRICHMENT_PROMPT = `Bitte formatiere das folgende Rohtranskript einer Fahrzeuginspektion in saubere, gut strukturierte Bulletpoints.
Korrigiere Rechtschreib- und Grammatikfehler, aber behalte alle technischen Informationen bei. Ergänze keine neuen Informationen.
Füge keine eigenen Bewertungen hinzu. Output soll nur inhalte des Transkripts wiedergeben.

Verwende dieses Format:
• Jeder wichtige Punkt als Bulletpoint
• Klare und professionelle Sprache
• Gruppiere ähnliche Themen zusammen
• Behalte alle Fahrzeugteile, Messungen und Befunde bei

ROHTRANSKRIPT:
{transcript}

Antworte NUR mit den formatierten Bulletpoints, ohne zusätzliche Erklärungen.`;

const EnrichmentSettings = ({ onLogout, showMessage, isAdmin = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [prompt, setPrompt] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [error, setError] = useState(null);
  const [backendStatus, setBackendStatus] = useState({ online: false, version: null, deployedAt: null, loading: true });

  // Data privacy consent state
  const [dataShareConsent, setDataShareConsent] = useState(null);
  const [dataShareConsentDate, setDataShareConsentDate] = useState(null);
  const [consentLoading, setConsentLoading] = useState(false);

  // User info state
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');

  // Use simplified setting hooks
  const [mediaButtonEnabled, toggleMediaButtonLocal] = useSetting('mediaButton');
  const [vibrateEnabled, toggleVibrate] = useSetting('vibrateOnRecording');

  // Wrap media button toggle to also sync to backend
  const toggleMediaButton = useCallback(async () => {
    const newValue = !mediaButtonEnabled;
    toggleMediaButtonLocal(); // Update local state immediately
    try {
      await updateUserSettings({ mediaButtonEnabled: newValue });
      console.log('Media button setting synced to backend:', newValue);
    } catch (err) {
      console.error('Failed to sync media button to backend:', err);
      // Don't revert - local setting is still valid
    }
  }, [mediaButtonEnabled, toggleMediaButtonLocal]);
  const { enabled: voiceTriggerEnabled, toggle: toggleVoiceTriggerLocal, triggerWord, setTriggerWord } = useVoiceTriggerSetting();
  const { stopWord, setStopWord } = useStopTriggerSetting();

  // Wrap voice trigger toggle to also sync to backend
  const toggleVoiceTrigger = useCallback(async () => {
    const newValue = !voiceTriggerEnabled;
    toggleVoiceTriggerLocal(); // Update local state immediately
    try {
      await updateUserSettings({ voiceTriggerEnabled: newValue });
      console.log('Voice trigger setting synced to backend:', newValue);
    } catch (err) {
      console.error('Failed to sync voice trigger to backend:', err);
      // Don't revert - local setting is still valid
    }
  }, [voiceTriggerEnabled, toggleVoiceTriggerLocal]);
  const { language: triggerLanguage, setLanguage: setTriggerLanguage } = useTriggerLanguageSetting();
  const { duration: chunkDuration, setDuration: setChunkDuration, min: minChunk, max: maxChunk } = useChunkDurationSetting();
  const [audioUploadEnabled, toggleAudioUpload] = useAudioUploadSetting();
  const [uploadRawChunksEnabled, toggleUploadRawChunks] = useUploadRawChunksSetting();
  const [playbackRawEnabled, togglePlaybackRaw] = usePlaybackRawSetting();
  const { level: noiseLevel, setLevel: setNoiseLevel } = useNoiseLevelSetting();
  const { level: pauseDetection, setLevel: setPauseDetection } = usePauseDetectionSetting();
  const [extendedErrorReportsEnabled, toggleExtendedErrorReports] = useExtendedErrorReportsSetting();
  const { volume: beepVolume, setVolume: setBeepVolume } = useBeepVolumeSetting();

  // System settings feature flags (from admin settings API)
  const [dangerZoneEnabledForAll, setDangerZoneEnabledForAll] = useState(false);

  const supportedLanguages = voiceTriggerService.getSupportedLanguages();

  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Delete all inspections state
  const [showDeleteInspectionsDialog, setShowDeleteInspectionsDialog] = useState(false);
  const [deleteInspectionsConfirmText, setDeleteInspectionsConfirmText] = useState('');
  const [isDeletingInspections, setIsDeletingInspections] = useState(false);
  const [deleteInspectionsError, setDeleteInspectionsError] = useState(null);

  const debounceTimerRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    loadSettings();
  }, []);

  // Fetch user info on mount
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const [attributes, currentUser] = await Promise.all([
          fetchUserAttributes(),
          getCurrentUser()
        ]);
        setUserEmail(attributes.email || '');
        setUserId(currentUser.userId || '');
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    loadUserInfo();
  }, []);

  useEffect(() => {
    const checkBackendHealth = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_ENDPOINT || '';
        const response = await fetch(`${apiUrl}/health`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        if (response.ok) {
          const data = await response.json();
          setBackendStatus({ online: true, version: data.version, deployedAt: data.deployed_at, loading: false });
        } else {
          setBackendStatus({ online: false, version: null, deployedAt: null, loading: false });
        }
      } catch (error) {
        setBackendStatus({ online: false, version: null, deployedAt: null, loading: false });
      }
    };

    checkBackendHealth();
  }, []);

  // Fetch feature flags from system settings
  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        console.log('[EnrichmentSettings] Fetching feature flags...');
        const response = await apiClient.get('/user/feature-flags');
        console.log('[EnrichmentSettings] Feature flags response:', response.data);
        const enabled = response.data?.dangerZoneEnabledForAll || false;
        console.log('[EnrichmentSettings] Setting dangerZoneEnabledForAll:', enabled);
        setDangerZoneEnabledForAll(enabled);
      } catch (error) {
        console.error('[EnrichmentSettings] Could not fetch feature flags:', error.message, error);
        setDangerZoneEnabledForAll(false);
      }
    };

    fetchFeatureFlags();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const settings = await fetchUserSettings();
      setPrompt(settings.enrichmentPrompt || DEFAULT_ENRICHMENT_PROMPT);
      setOriginalPrompt(settings.enrichmentPrompt || DEFAULT_ENRICHMENT_PROMPT);
      setIsDefault(settings.isDefault);
      setDataShareConsent(settings.dataShareConsent);
      setDataShareConsentDate(settings.dataShareConsentDate);

      // Sync media button setting from backend to local storage
      if (settings.mediaButtonEnabled !== undefined) {
        const localMediaButton = localStorage.getItem('media_button_enabled');
        if (localMediaButton === null || JSON.parse(localMediaButton) !== settings.mediaButtonEnabled) {
          localStorage.setItem('media_button_enabled', JSON.stringify(settings.mediaButtonEnabled));
          // Trigger re-render of the toggle
          window.dispatchEvent(new CustomEvent('mediaButtonSettingChanged', {
            detail: { enabled: settings.mediaButtonEnabled }
          }));
        }
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError(t('common.error'));
      setPrompt(DEFAULT_ENRICHMENT_PROMPT);
      setOriginalPrompt(DEFAULT_ENRICHMENT_PROMPT);
    } finally {
      setLoading(false);
      // Mark initial load as complete after a short delay
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    }
  };

  // Auto-save function
  const performSave = useCallback(async (promptToSave) => {
    if (!promptToSave.includes('{transcript}')) {
      setError(t('settings.placeholder_hint'));
      setSaveStatus('error');
      return;
    }

    if (promptToSave.length > 4000) {
      setError(t('settings.prompt_too_long') || 'Prompt is too long');
      setSaveStatus('error');
      return;
    }

    try {
      setSaveStatus('saving');
      setError(null);

      await updateUserSettings(promptToSave);
      setOriginalPrompt(promptToSave);
      setIsDefault(false);
      setSaveStatus('saved');

      // Reset to idle after showing "saved" for 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message || t('common.error'));
      setSaveStatus('error');
    }
  }, [t]);

  // Debounced auto-save effect
  useEffect(() => {
    // Skip auto-save during initial load
    if (isInitialLoadRef.current || loading) {
      return;
    }

    // Skip if prompt hasn't changed from original
    if (prompt === originalPrompt) {
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer (1 second delay)
    debounceTimerRef.current = setTimeout(() => {
      performSave(prompt);
    }, 1000);

    // Cleanup on unmount or when prompt changes
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [prompt, originalPrompt, loading, performSave]);

  const handleReset = () => {
    setPrompt(DEFAULT_ENRICHMENT_PROMPT);
    setError(null);
    setSaveStatus('idle');
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleTriggerWordChange = (e) => {
    setTriggerWord(e.target.value);
  };

  const handleTriggerWordBlur = async () => {
    // Sync to backend when user finishes editing
    try {
      await updateUserSettings({ voiceTriggerStartPhrase: triggerWord });
      console.log('Start phrase synced to backend:', triggerWord);
    } catch (err) {
      console.error('Failed to sync start phrase to backend:', err);
    }
  };

  const handleStopWordChange = (e) => {
    setStopWord(e.target.value);
  };

  const handleStopWordBlur = async () => {
    // Sync to backend when user finishes editing
    try {
      await updateUserSettings({ voiceTriggerStopPhrase: stopWord });
      console.log('Stop phrase synced to backend:', stopWord);
    } catch (err) {
      console.error('Failed to sync stop phrase to backend:', err);
    }
  };

  const handleTriggerLanguageChange = (e) => {
    setTriggerLanguage(e.target.value);
  };

  const handleChunkDurationChange = (e) => {
    setChunkDuration(parseInt(e.target.value, 10));
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (secs === 0) return `${mins} min`;
    return `${mins}:${secs.toString().padStart(2, '0')} min`;
  };

  const handleTestEnrichment = () => {
    navigate('/settings/test');
  };

  const handlePresetChange = (preset) => {
    const config = ENVIRONMENT_PRESETS[preset];
    if (config) {
      setNoiseLevel(config.noiseLevel);
      setPauseDetection(config.pauseDetection);
      setBeepVolume(config.beepVolume);
    }
  };

  const getNoiseLevelLabel = (level) => {
    const labels = ['Quiet Office', 'Quiet', 'Workshop', 'Loud', 'Very Loud'];
    return labels[level] || 'Workshop';
  };

  const getPauseLabel = (level) => {
    const labels = ['Very Sensitive', 'Sensitive', 'Normal', 'Relaxed', 'Very Relaxed'];
    return labels[level] || 'Normal';
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== t('settings.delete_confirm_word')) {
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteAccount();
      // Account deleted successfully, log out the user
      setShowDeleteDialog(false);
      if (onLogout) {
        onLogout();
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      setDeleteError(err.message || t('common.error'));
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = () => {
    setDeleteConfirmText('');
    setDeleteError(null);
    setShowDeleteDialog(true);
  };

  const handleDeleteAllInspections = async () => {
    if (deleteInspectionsConfirmText !== t('settings.delete_confirm_word')) {
      return;
    }

    setIsDeletingInspections(true);
    setDeleteInspectionsError(null);

    try {
      await deleteAllInspections();
      setShowDeleteInspectionsDialog(false);
      if (showMessage) {
        showMessage(t('settings.delete_inspections_success'), 'success');
      }
    } catch (err) {
      console.error('Error deleting all inspections:', err);
      setDeleteInspectionsError(err.message || t('common.error'));
    } finally {
      setIsDeletingInspections(false);
    }
  };

  const openDeleteInspectionsDialog = () => {
    setDeleteInspectionsConfirmText('');
    setDeleteInspectionsError(null);
    setShowDeleteInspectionsDialog(true);
  };

  const handleConsentToggle = async () => {
    const newConsent = !dataShareConsent;
    setConsentLoading(true);
    try {
      await updateUserConsent(newConsent);
      setDataShareConsent(newConsent);
      setDataShareConsentDate(new Date().toISOString());
    } catch (err) {
      console.error('Error updating consent:', err);
      setError(err.message || t('common.error'));
    } finally {
      setConsentLoading(false);
    }
  };

  // Render save status indicator
  const renderSaveStatus = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <span className="flex items-center gap-1 text-text-muted">
            <Loader2 size={14} className="animate-spin" />
            {t('settings.saving')}
          </span>
        );
      case 'saved':
        return (
          <span className="flex items-center gap-1 text-success">
            <Check size={14} />
            {t('settings.saved')}
          </span>
        );
      case 'error':
        return (
          <span className="text-error">
            {t('common.error')}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24 flex flex-col">
      {/* App Bar */}
      <header className="app-bar">
        <button onClick={handleBack} className="cursor-pointer hover:opacity-80">
          <Logo size={40} />
        </button>
        <h1 className="flex-1 text-white font-normal text-xl">
          {t('settings.title')}
        </h1>
        <AppBarMenu onLogout={onLogout} isAdmin={isAdmin} />
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full px-4 lg:px-8 py-6 space-y-4">
        {/* General Settings Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-bold text-secondary mb-3">
            {t('settings.general')}
          </h2>
          <LanguageSwitcher />

          <hr className="border-border my-3" />

          {/* User Info */}
          <div className="space-y-1 text-sm mb-4">
            <h3 className="font-semibold text-text mb-2">{t('settings.user')}</h3>
            <div className="flex justify-between">
              <span className="text-text-muted">{t('settings.email')}</span>
              <span className="font-mono text-xs text-text truncate ml-2 max-w-[200px]">{userEmail || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">{t('settings.user_id')}</span>
              <span className="font-mono text-xs text-text">{userId ? userId.split('-').pop() : '-'}</span>
            </div>
          </div>

          <hr className="border-border my-3" />

          {/* Version Info */}
          <div className="space-y-4 text-sm">
            {/* Client */}
            <div className="space-y-1">
              <h3 className="font-semibold text-text mb-2">{t('settings.client')}</h3>
              <div className="flex justify-between">
                <span className="text-text-muted">{t('settings.url')}</span>
                <span className="font-mono text-xs text-text">{window.location.origin}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">{t('settings.version')}</span>
                <span className="font-mono text-xs text-text">{packageJson.version}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">{t('settings.deployed')}</span>
                <span className="font-mono text-xs text-text">{formatTimestamp(process.env.REACT_APP_BUILD_TIME)}</span>
              </div>
            </div>

            {/* Backend */}
            <div className="space-y-1">
              <h3 className="font-semibold text-text mb-2 flex items-center gap-2">
                {t('settings.backend')}
                {backendStatus.loading ? (
                  <Loader2 size={12} className="animate-spin text-text-muted" />
                ) : backendStatus.online ? (
                  <span className="w-2 h-2 rounded-full bg-success" title={t('settings.backend_healthy')} />
                ) : (
                  <span className="w-2 h-2 rounded-full bg-error" title={t('settings.backend_unhealthy')} />
                )}
              </h3>
              <div className="flex justify-between">
                <span className="text-text-muted">{t('settings.url')}</span>
                <span className="font-mono text-xs text-text truncate ml-2">{process.env.REACT_APP_API_ENDPOINT || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">{t('settings.version')}</span>
                <span className="font-mono text-xs text-text">{backendStatus.version || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">{t('settings.deployed')}</span>
                <span className="font-mono text-xs text-text">{formatTimestamp(backendStatus.deployedAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Input Controls Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-bold text-secondary mb-4">
            {t('settings.input_controls')}
          </h2>

          {/* Media Button Toggle */}
          <button
            onClick={toggleMediaButton}
            className="w-full flex items-center gap-3 py-3 cursor-pointer text-left"
          >
            <Bluetooth size={24} className={mediaButtonEnabled ? 'text-primary' : 'text-text-muted'} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">{t('settings.media_button')}</p>
              <p className="text-xs text-text-muted">{t('settings.media_button_desc')}</p>
            </div>
            <div className={`w-12 h-6 rounded-full transition-colors ${mediaButtonEnabled ? 'bg-success' : 'bg-border'}`}>
              <div className={`w-5 h-5 rounded-full bg-white shadow mt-0.5 transition-transform ${mediaButtonEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </div>
          </button>

          <hr className="border-border my-2" />

          {/* Voice Trigger Toggle */}
          <button
            onClick={toggleVoiceTrigger}
            className="w-full flex items-center gap-3 py-3 cursor-pointer text-left"
          >
            <Mic size={24} className={voiceTriggerEnabled ? 'text-primary' : 'text-text-muted'} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">{t('settings.voice_trigger')}</p>
              <p className="text-xs text-text-muted">{t('settings.voice_trigger_desc')}</p>
            </div>
            <div className={`w-12 h-6 rounded-full transition-colors ${voiceTriggerEnabled ? 'bg-success' : 'bg-border'}`}>
              <div className={`w-5 h-5 rounded-full bg-white shadow mt-0.5 transition-transform ${voiceTriggerEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </div>
          </button>

          {/* Voice Trigger Configuration (shown when enabled) */}
          {voiceTriggerEnabled && (
            <>
              <hr className="border-border my-2" />

              {/* Voice Trigger Language */}
              <div className="flex items-start gap-3 py-3">
                <Globe size={24} className="text-primary flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-text">{t('settings.trigger_language')}</p>
                  <p className="text-xs text-text-muted mb-2">{t('settings.trigger_language_desc')}</p>
                  <select
                    value={triggerLanguage}
                    onChange={handleTriggerLanguageChange}
                    className="input py-2 px-3 text-sm w-auto min-w-[180px]"
                  >
                    {supportedLanguages.map(lang => (
                      <option key={lang.code} value={lang.code}>{lang.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <hr className="border-border my-2" />

              {/* Trigger Words Configuration */}
              <div className="py-3 space-y-4">
                {/* Start Phrase */}
                <div className="flex items-start gap-3">
                  <div className="w-6 flex-shrink-0" />
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-text block mb-1">{t('settings.start_phrase')}</label>
                    <input
                      type="text"
                      value={triggerWord}
                      onChange={handleTriggerWordChange}
                      onBlur={handleTriggerWordBlur}
                      placeholder="Aufnahme Start"
                      className="input py-2 px-3 text-sm w-auto min-w-[150px]"
                    />
                    <p className="text-xs text-text-muted mt-1">
                      {t('settings.start_phrase_hint')}
                    </p>
                  </div>
                </div>

                {/* Stop Phrase */}
                <div className="flex items-start gap-3">
                  <div className="w-6 flex-shrink-0" />
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-text block mb-1">{t('settings.stop_phrase')}</label>
                    <input
                      type="text"
                      value={stopWord}
                      onChange={handleStopWordChange}
                      onBlur={handleStopWordBlur}
                      placeholder="Aufnahme Stopp"
                      className="input py-2 px-3 text-sm w-auto min-w-[150px]"
                    />
                    <p className="text-xs text-text-muted mt-1">
                      {t('settings.stop_phrase_hint')}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Vibrate on Recording Toggle - only show on devices that support it (Android) */}
          {navigator.vibrate && (
            <>
              <hr className="border-border my-2" />
              <button
                onClick={toggleVibrate}
                className="w-full flex items-center gap-3 py-3 cursor-pointer text-left"
              >
                <Vibrate size={24} className={vibrateEnabled ? 'text-primary' : 'text-text-muted'} />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-text">{t('settings.vibrate_recording')}</p>
                  <p className="text-xs text-text-muted">{t('settings.vibrate_recording_desc')}</p>
                </div>
                <div className={`w-12 h-6 rounded-full transition-colors ${vibrateEnabled ? 'bg-success' : 'bg-border'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow mt-0.5 transition-transform ${vibrateEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </div>
              </button>
            </>
          )}

        </div>

        {/* Recording Settings Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-bold text-secondary mb-4">
            {t('settings.recording_settings')}
          </h2>

          {/* Environment Presets */}
          <div className="py-3">
            <p className="text-sm font-semibold text-text mb-2">{t('settings.environment_preset')}</p>
            <div className="flex gap-2 flex-wrap">
              {['quietOffice', 'workshop', 'outdoor', 'veryLoud'].map((preset) => (
                <button
                  key={preset}
                  onClick={() => handlePresetChange(preset)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    noiseLevel === ENVIRONMENT_PRESETS[preset].noiseLevel &&
                    pauseDetection === ENVIRONMENT_PRESETS[preset].pauseDetection &&
                    beepVolume === ENVIRONMENT_PRESETS[preset].beepVolume
                      ? 'bg-primary text-white border-primary'
                      : 'bg-surface border-border text-text hover:border-primary'
                  }`}
                >
                  {t(`settings.preset_${preset.replace(/([A-Z])/g, '_$1').toLowerCase()}`)}
                </button>
              ))}
            </div>
          </div>

          <hr className="border-border my-2" />

          {/* Background Noise Level Slider */}
          <div className="flex items-start gap-3 py-3">
            <Volume2 size={24} className="text-primary flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">{t('settings.noise_level')}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs text-text-muted">{t('settings.noise_quiet')}</span>
                <input
                  type="range"
                  min={0}
                  max={4}
                  step={1}
                  value={noiseLevel}
                  onChange={(e) => setNoiseLevel(parseInt(e.target.value, 10))}
                  className="flex-1 h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <span className="text-xs text-text-muted">{t('settings.noise_loud')}</span>
              </div>
              <p className="text-xs text-text-muted mt-1 text-center">
                {getNoiseLevelLabel(noiseLevel)} ({getNoiseLevelDb(noiseLevel)} dB)
              </p>
            </div>
          </div>

          <hr className="border-border my-2" />

          {/* Pause Detection Slider */}
          <div className="flex items-start gap-3 py-3">
            <Pause size={24} className="text-primary flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">{t('settings.pause_detection')}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs text-text-muted">{t('settings.pause_sensitive')}</span>
                <input
                  type="range"
                  min={0}
                  max={4}
                  step={1}
                  value={pauseDetection}
                  onChange={(e) => setPauseDetection(parseInt(e.target.value, 10))}
                  className="flex-1 h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <span className="text-xs text-text-muted">{t('settings.pause_relaxed')}</span>
              </div>
              <p className="text-xs text-text-muted mt-1 text-center">
                {getPauseLabel(pauseDetection)} ({getPauseThresholdDb(pauseDetection)} dB)
              </p>
            </div>
          </div>

          <hr className="border-border my-2" />

          {/* Beep Volume Slider */}
          <div className="flex items-start gap-3 py-3">
            <Volume2 size={24} className={beepVolume > 0 ? 'text-primary' : 'text-text-muted'} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">{t('settings.beep_volume')}</p>
              <p className="text-xs text-text-muted mb-2">{t('settings.beep_volume_desc')}</p>
              <div className="flex items-center gap-3">
                <span className="text-xs text-text-muted">{t('settings.beep_off')}</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={10}
                  value={beepVolume}
                  onChange={(e) => setBeepVolume(parseInt(e.target.value, 10))}
                  className="flex-1 h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <span className="text-xs text-text-muted">{t('settings.beep_max')}</span>
              </div>
              <p className="text-xs text-text-muted mt-1 text-center">
                {beepVolume === 0 ? t('settings.beep_muted') : `${beepVolume}%`}
              </p>
            </div>
          </div>

          <hr className="border-border my-2" />

          {/* Chunk Duration Slider */}
          <div className="flex items-start gap-3 py-3">
            <Clock size={24} className="text-primary flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">{t('settings.chunk_duration')}</p>
              <p className="text-xs text-text-muted mb-3">{t('settings.chunk_duration_desc')}</p>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={minChunk}
                  max={maxChunk}
                  step={30}
                  value={chunkDuration}
                  onChange={handleChunkDurationChange}
                  className="flex-1 h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <span className="text-sm font-mono text-text min-w-[70px] text-right">
                  {formatDuration(chunkDuration)}
                </span>
              </div>
              <div className="flex justify-between text-xs text-text-muted mt-1">
                <span>{formatDuration(minChunk)}</span>
                <span>{formatDuration(maxChunk)}</span>
              </div>
            </div>
          </div>

        </div>

        {/* Enrichment Settings Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-bold text-secondary mb-3">
            {t('settings.enrichment_prompt')}
          </h2>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <>
              {/* Info Box */}
              <div className="p-3 bg-background rounded-lg mb-4">
                <p className="text-sm text-text">
                  {t('settings.enrichment_description')}{' '}
                  {t('settings.placeholder_hint')}
                </p>
              </div>

              {/* Status Badge */}
              <div className="mb-4">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    isDefault
                      ? 'bg-gray-200 text-text-muted'
                      : 'bg-primary text-white'
                  }`}
                >
                  {isDefault ? t('settings.default_prompt') : t('settings.custom_prompt')}
                </span>
              </div>

              {/* Error Alert */}
              {error && <Alert severity="error" className="mb-4">{error}</Alert>}

              {/* Prompt Textarea */}
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={12}
                placeholder={t('settings.prompt_placeholder')}
                className="input font-mono text-sm leading-relaxed resize-y mb-2"
              />

              {/* Character Count and Save Status */}
              <div className="flex justify-between items-center mb-4">
                <div className="text-xs">
                  {renderSaveStatus()}
                </div>
                <p
                  className={`text-xs ${
                    prompt.length > 4000 ? 'text-error' : 'text-text-muted'
                  }`}
                >
                  {prompt.length} / 4000
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleReset}
                  disabled={saveStatus === 'saving'}
                  className="btn-secondary flex-1 flex items-center justify-center gap-2"
                >
                  <RotateCcw size={20} />
                  {t('settings.reset')}
                </button>
                <button
                  onClick={handleTestEnrichment}
                  disabled={saveStatus === 'saving'}
                  className="btn-secondary flex-1 flex items-center justify-center gap-2"
                >
                  <FlaskConical size={20} />
                  {t('settings.test_enrichment')}
                </button>
              </div>

              {/* Hint */}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>{t('settings.note')}:</strong> {t('settings.changes_note')} {t('settings.reprocess_note')}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Data Privacy Card */}
        <DataPrivacyCard
          consent={dataShareConsent}
          consentDate={dataShareConsentDate}
          onToggle={handleConsentToggle}
          loading={consentLoading}
        />

        {/* Developer Tools Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-bold text-secondary mb-3">
            {t('settings.developer_tools')}
          </h2>
          <p className="text-xs text-text-muted mb-4">
            {t('settings.developer_tools_desc')}
          </p>

          {/* Extended Error Reports Toggle */}
          <button
            onClick={toggleExtendedErrorReports}
            className="w-full flex items-center gap-3 py-3 cursor-pointer text-left"
          >
            <AlertCircle size={24} className={extendedErrorReportsEnabled ? 'text-primary' : 'text-text-muted'} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">{t('settings.extended_error_reports')}</p>
              <p className="text-xs text-text-muted">{t('settings.extended_error_reports_desc')}</p>
            </div>
            <div className={`w-12 h-6 rounded-full transition-colors ${extendedErrorReportsEnabled ? 'bg-success' : 'bg-border'}`}>
              <div className={`w-5 h-5 rounded-full bg-white shadow mt-0.5 transition-transform ${extendedErrorReportsEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </div>
          </button>

          <hr className="border-border my-2" />

          {/* Upload Raw Chunks Toggle */}
          <button
            onClick={toggleUploadRawChunks}
            className="w-full flex items-center gap-3 py-3 cursor-pointer text-left"
          >
            <Bug size={24} className={uploadRawChunksEnabled ? 'text-primary' : 'text-text-muted'} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">{t('settings.upload_raw_chunks')}</p>
              <p className="text-xs text-text-muted">{t('settings.upload_raw_chunks_desc')}</p>
            </div>
            <div className={`w-12 h-6 rounded-full transition-colors ${uploadRawChunksEnabled ? 'bg-success' : 'bg-border'}`}>
              <div className={`w-5 h-5 rounded-full bg-white shadow mt-0.5 transition-transform ${uploadRawChunksEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </div>
          </button>

          <hr className="border-border my-2" />

          {/* Playback Raw Toggle */}
          <button
            onClick={togglePlaybackRaw}
            className="w-full flex items-center gap-3 py-3 cursor-pointer text-left"
          >
            <Play size={24} className={playbackRawEnabled ? 'text-primary' : 'text-text-muted'} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">{t('settings.playback_raw')}</p>
              <p className="text-xs text-text-muted">{t('settings.playback_raw_desc')}</p>
            </div>
            <div className={`w-12 h-6 rounded-full transition-colors ${playbackRawEnabled ? 'bg-success' : 'bg-border'}`}>
              <div className={`w-5 h-5 rounded-full bg-white shadow mt-0.5 transition-transform ${playbackRawEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </div>
          </button>

          <hr className="border-border my-2" />

          {/* Audio File Upload Toggle */}
          <button
            onClick={toggleAudioUpload}
            className="w-full flex items-center gap-3 py-3 cursor-pointer text-left"
          >
            <Upload size={24} className={audioUploadEnabled ? 'text-primary' : 'text-text-muted'} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">{t('settings.audio_file_upload')}</p>
              <p className="text-xs text-text-muted">{t('settings.audio_file_upload_desc')}</p>
            </div>
            <div className={`w-12 h-6 rounded-full transition-colors ${audioUploadEnabled ? 'bg-success' : 'bg-border'}`}>
              <div className={`w-5 h-5 rounded-full bg-white shadow mt-0.5 transition-transform ${audioUploadEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </div>
          </button>
        </div>

        {/* Danger Zone Card - Visible only if enabled in system settings */}
        {dangerZoneEnabledForAll && (
          <div className="card-elevated p-4 border-2 border-red-200">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={20} className="text-red-600" />
            <h2 className="text-lg font-bold text-red-600">
              {t('settings.danger_zone')}
            </h2>
          </div>

          <p className="text-sm text-text-muted mb-4">
            {t('settings.danger_zone_description')}
          </p>

          <div className="space-y-3">
            <button
              onClick={openDeleteInspectionsDialog}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
            >
              <Trash2 size={20} />
              {t('settings.delete_all_inspections')}
            </button>

            <button
              onClick={openDeleteDialog}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
            >
              <Trash2 size={20} />
              {t('settings.delete_account')}
            </button>
          </div>
        </div>
        )}

      </main>

      {/* Delete Account Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => !isDeleting && setShowDeleteDialog(false)} />
          <div className="relative bg-surface rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={24} className="text-red-600" />
              <h2 className="text-lg font-bold text-red-600">
                {t('settings.delete_account_title')}
              </h2>
            </div>

            <div className="space-y-4">
              <p className="text-text">
                {t('settings.delete_account_warning')}
              </p>

              <ul className="list-disc list-inside text-sm text-text-muted space-y-1">
                <li>{t('settings.delete_item_inspections')}</li>
                <li>{t('settings.delete_item_recordings')}</li>
                <li>{t('settings.delete_item_settings')}</li>
                <li>{t('settings.delete_item_account')}</li>
              </ul>

              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">
                  {t('settings.delete_account_confirm_instruction')}
                </p>
              </div>

              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder={t('settings.delete_confirm_word')}
                disabled={isDeleting}
                className="input w-full text-center font-mono tracking-widest"
              />

              {deleteError && (
                <Alert severity="error">{deleteError}</Alert>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== t('settings.delete_confirm_word') || isDeleting}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg font-medium transition-colors"
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    {t('settings.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 size={20} />
                    {t('settings.delete_account')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Inspections Confirmation Dialog */}
      {showDeleteInspectionsDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => !isDeletingInspections && setShowDeleteInspectionsDialog(false)} />
          <div className="relative bg-surface rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={24} className="text-red-500" />
              <h2 className="text-lg font-bold text-red-500">
                {t('settings.delete_all_inspections_title')}
              </h2>
            </div>

            <div className="space-y-4">
              <p className="text-text">
                {t('settings.delete_all_inspections_warning')}
              </p>

              <ul className="list-disc list-inside text-sm text-text-muted space-y-1">
                <li>{t('settings.delete_item_inspections')}</li>
                <li>{t('settings.delete_item_recordings')}</li>
              </ul>

              <p className="text-sm text-text-muted">
                {t('settings.delete_all_inspections_note')}
              </p>

              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">
                  {t('settings.delete_account_confirm_instruction')}
                </p>
              </div>

              <input
                type="text"
                value={deleteInspectionsConfirmText}
                onChange={(e) => setDeleteInspectionsConfirmText(e.target.value.toUpperCase())}
                placeholder={t('settings.delete_confirm_word')}
                disabled={isDeletingInspections}
                className="input w-full text-center font-mono tracking-widest"
              />

              {deleteInspectionsError && (
                <Alert severity="error">{deleteInspectionsError}</Alert>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeleteInspectionsDialog(false)}
                disabled={isDeletingInspections}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteAllInspections}
                disabled={deleteInspectionsConfirmText !== t('settings.delete_confirm_word') || isDeletingInspections}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white rounded-lg font-medium transition-colors"
              >
                {isDeletingInspections ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    {t('settings.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 size={20} />
                    {t('settings.delete_all_inspections')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrichmentSettings;
